import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Plus, Minus, Trash2, X, Wallet, TrendingDown, PiggyBank,
  AlertTriangle, CalendarClock, Check, Layers, Pencil, Combine, Link2, Unlink,
  History, Undo2, RotateCcw
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { BudgetSheet, BudgetSheetHandle } from './BudgetSheet';
import {
  BudgetMaquina, BudgetObra, BudgetResumo, EtapaCol, GrupoRubrica,
  gruposDaObra, linhasDaObra, moeda, ordenarGrupos, parseValor, recalcularObra,
  somarGrupos, totaisPorGrupo, SEM_ETAPA, SEM_RUBRICA
} from './budget-types';

interface ResumoDoPonto {
  linhas: number;
  budgetTotal: number;
  gastoTotal: number;
}

interface PontoHistorico extends ResumoDoPonto {
  id: string;
  acao: string;
  descricao: string;
  usuarioNome: string | null;
  createdAt: string;
}

/** "há 3 min", "há 2 h" — o histórico é lido pelo tempo, não pela data */
function tempoAtras(iso: string) {
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function BudgetPage() {
  const { projeto } = useProjeto();
  const [resumo, setResumo] = useState<BudgetResumo | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [obraSelecionadaId, setObraSelecionadaId] = useState<string | null>(null);
  // Colunas de etapa criadas na tela e ainda sem lançamento, por rubrica
  const [etapasExtras, setEtapasExtras] = useState<Record<string, EtapaCol[]>>({});
  // Rubricas em foco: começa com uma só e é o que a planilha e os cards somam
  const [rubricasAtivas, setRubricasAtivas] = useState<string[]>([]);
  const [versaoPlanilha, setVersaoPlanilha] = useState(0);

  const [mostrarNovaObra, setMostrarNovaObra] = useState(false);
  const [novaObraNome, setNovaObraNome] = useState('');
  const [mostrarNovaEtapa, setMostrarNovaEtapa] = useState(false);
  const [novaEtapaNome, setNovaEtapaNome] = useState('');
  const [novaEtapaTipo, setNovaEtapaTipo] = useState<'realizado' | 'previsto'>('realizado');
  const [novaEtapaRubrica, setNovaEtapaRubrica] = useState('');
  const [mostrarNovaRubrica, setMostrarNovaRubrica] = useState(false);
  const [novaRubricaNome, setNovaRubricaNome] = useState('');
  const [novaRubricaVinculada, setNovaRubricaVinculada] = useState(true);

  // Modal "Editar nomes": título da planilha (obra), das rubricas e das colunas
  const [mostrarEditarNomes, setMostrarEditarNomes] = useState(false);
  const [editNomeObra, setEditNomeObra] = useState('');
  const [editNomesRubricas, setEditNomesRubricas] = useState<Record<string, string>>({});
  const [editNomesEtapas, setEditNomesEtapas] = useState<Record<string, string>>({});

  // Modal "Juntar abas"
  const [mostrarJuntar, setMostrarJuntar] = useState(false);
  const [juntarOrigens, setJuntarOrigens] = useState<Record<string, string>>({});
  const [juntarRubricaDestino, setJuntarRubricaDestino] = useState('');

  const sheetRef = useRef<BudgetSheetHandle>(null);
  const [mostrarLote, setMostrarLote] = useState(false);
  const [loteRubricaSel, setLoteRubricaSel] = useState('');
  const [loteEtapaSel, setLoteEtapaSel] = useState('');
  const [loteNovaEtapaNome, setLoteNovaEtapaNome] = useState('');
  const [loteNovaEtapaTipo, setLoteNovaEtapaTipo] = useState<'realizado' | 'previsto'>('realizado');
  const [loteValor, setLoteValor] = useState('');
  const [loteModo, setLoteModo] = useState<'por_maquina' | 'rateio' | 'proporcional'>('proporcional');
  const [loteAlvo, setLoteAlvo] = useState<'selecionadas' | 'todas'>('todas');
  const [loteSelecionadas, setLoteSelecionadas] = useState<string[]>([]);
  // Modo "selecionar na planilha": esconde o modal para o usuário arrastar na grade
  const [selecionandoNaPlanilha, setSelecionandoNaPlanilha] = useState(false);

  // Histórico de estado da obra (o desfazer que sobrevive a recarregar a página)
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [historico, setHistorico] = useState<PontoHistorico[]>([]);
  const [historicoAtual, setHistoricoAtual] = useState<ResumoDoPonto | null>(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const carregar = useCallback(async () => {
    if (!projeto?.id) return;
    try {
      const res = await apiService.buscarBudget(projeto.id);
      if (res.success && res.data) {
        const dados = res.data as BudgetResumo;
        setResumo(dados);
        setObraSelecionadaId((atual) => {
          if (atual && dados.obras.some((o) => o.id === atual)) return atual;
          return dados.obras[0]?.id ?? null;
        });
      } else {
        setResumo(null);
      }
    } catch {
      setResumo(null);
    } finally {
      setLoading(false);
    }
  }, [projeto?.id]);

  useEffect(() => {
    setLoading(true);
    carregar();
  }, [carregar]);

  useEffect(() => {
    setEtapasExtras({});
    setRubricasAtivas([]);
  }, [obraSelecionadaId]);

  const obra = resumo?.obras.find((o) => o.id === obraSelecionadaId) ?? null;

  // Grupos de colunas: as rubricas da obra + as colunas de etapa criadas na tela
  const gruposCompletos: GrupoRubrica[] = useMemo(() => {
    if (!obra) return [];
    return gruposDaObra(obra).map((g) => {
      const extras = (etapasExtras[g.id] ?? []).filter((e) => !g.etapas.some((b) => b.key === e.key));
      return { ...g, etapas: [...g.etapas, ...extras] };
    });
  }, [obra, etapasExtras]);

  // Abre com uma rubrica só; ao trocar de obra ou apagar uma rubrica, volta para a primeira
  useEffect(() => {
    if (gruposCompletos.length === 0) return;
    setRubricasAtivas((atual) => {
      const validas = atual.filter((id) => gruposCompletos.some((g) => g.id === id));
      if (validas.length === 0) return [gruposCompletos[0].id];
      return validas.length === atual.length ? atual : validas;
    });
  }, [gruposCompletos]);

  // Colunas da planilha: as rubricas desvinculadas vão para a direita, junto do
  // bloco de linhas delas no final
  const grupos = useMemo(
    () => ordenarGrupos(gruposCompletos.filter((g) => rubricasAtivas.includes(g.id))),
    [gruposCompletos, rubricasAtivas]
  );

  const linhas = useMemo(() => (obra ? linhasDaObra(obra, rubricasAtivas) : []), [obra, rubricasAtivas]);

  // Nome de TODAS as rubricas, não só das ativas: o aviso de exclusão da planilha
  // precisa citar as que estão fora da tela e vão junto mesmo assim
  const nomesDasRubricas = useMemo(
    () => Object.fromEntries(gruposCompletos.map((g) => [g.id, g.nome])),
    [gruposCompletos]
  );

  // Totais das rubricas ativas — é o que os cards do topo mostram
  const totaisDosGrupos = useMemo(() => (obra ? totaisPorGrupo(obra) : {}), [obra]);
  const totais = useMemo(() => somarGrupos(totaisDosGrupos, rubricasAtivas), [totaisDosGrupos, rubricasAtivas]);

  /** Clique no card: foca só naquela rubrica */
  const focarRubrica = (id: string) => setRubricasAtivas([id]);

  /** "+" soma a rubrica ao budget, "−" tira (a última ativa não sai) */
  const alternarRubrica = (id: string) =>
    setRubricasAtivas((atual) => {
      if (!atual.includes(id)) return gruposCompletos.filter((g) => atual.includes(g.id) || g.id === id).map((g) => g.id);
      return atual.length > 1 ? atual.filter((x) => x !== id) : atual;
    });

  // ===== Sincronização otimista (a planilha edita; os cards recalculam) =====

  const atualizarObraLocal = (obraId: string, transformar: (o: BudgetObra) => BudgetObra) => {
    setResumo((atual) => {
      if (!atual) return atual;
      const obras = atual.obras.map((o) => (o.id === obraId ? recalcularObra(transformar(o)) : o));
      const totaisGerais = obras.reduce(
        (acc, o) => ({
          budgetTotal: acc.budgetTotal + o.totais.budgetTotal,
          gastoRealizado: acc.gastoRealizado + o.totais.gastoRealizado,
          gastoPrevisto: acc.gastoPrevisto + o.totais.gastoPrevisto,
          foraDoBudget: acc.foraDoBudget + o.totais.foraDoBudget
        }),
        { budgetTotal: 0, gastoRealizado: 0, gastoPrevisto: 0, foraDoBudget: 0 }
      );
      return { ...atual, obras, totais: { ...totaisGerais, saldo: totaisGerais.budgetTotal - totaisGerais.gastoRealizado } };
    });
  };

  const atualizarMaquinaLocal = (obraId: string, maquinaId: string, transformar: (m: BudgetMaquina) => BudgetMaquina) => {
    atualizarObraLocal(obraId, (o) => ({
      ...o,
      maquinas: o.maquinas.map((m) => (m.id === maquinaId ? transformar(m) : m))
    }));
  };

  // Salva em background; em caso de erro recarrega tudo e reconstrói a planilha
  const salvarSilencioso = async (chamada: () => Promise<{ success: boolean; message?: string }>) => {
    setSalvando(true);
    try {
      const res = await chamada();
      if (!res.success) {
        alert(res.message || 'Erro ao salvar');
        await carregar();
        setVersaoPlanilha((v) => v + 1);
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar');
      await carregar();
      setVersaoPlanilha((v) => v + 1);
    } finally {
      setSalvando(false);
    }
  };

  // A linha da planilha é uma só: renomear tem que valer em todas as rubricas.
  // Nas desvinculadas a linha é própria, então o novo nome fica dentro da rubrica.
  const handleRenomearMaquina = (de: string, para: string, rubricaId?: string) => {
    if (!obra) return;
    const alvo = (m: BudgetMaquina) =>
      m.nome === de && (!rubricaId || (m.rubricaId ?? SEM_RUBRICA) === rubricaId);
    atualizarObraLocal(obra.id, (o) => ({
      ...o,
      maquinas: o.maquinas.map((m) => (alvo(m) ? { ...m, nome: para } : m))
    }));
    salvarSilencioso(() => apiService.renomearMaquinaBudget(obra.id, {
      de,
      para,
      rubricaId: rubricaId && rubricaId !== SEM_RUBRICA ? rubricaId : null
    }));
  };

  const handleEditarBudget = (id: string, valor: number) => {
    if (!obra) return;
    atualizarMaquinaLocal(obra.id, id, (m) => ({ ...m, budget: valor }));
    salvarSilencioso(() => apiService.atualizarMaquinaBudget(id, { budget: valor }));
  };

  const handleEditarEtapa = (id: string, col: EtapaCol, valor: number) => {
    if (!obra) return;
    atualizarMaquinaLocal(obra.id, id, (m) => ({
      ...m,
      gastos: [
        ...m.gastos.filter((g) => (g.etapa ?? SEM_ETAPA) !== col.key),
        ...(valor > 0 ? [{ id: `local-${id}-${col.key}`, etapa: col.nome, tipo: col.tipo, valor, data: new Date().toISOString() }] : [])
      ]
    }));
    salvarSilencioso(() => apiService.definirGastoEtapaBudget(id, { etapa: col.nome ?? undefined, tipo: col.tipo, valor }));
  };

  const handleCriarLinha = async (nome: string, rubricaId: string, budget: number): Promise<string | null> => {
    if (!obra) return null;
    setSalvando(true);
    try {
      const res = await apiService.criarMaquinaBudget(obra.id, {
        nome,
        budget,
        rubricaId: rubricaId === SEM_RUBRICA ? null : rubricaId
      });
      if (!res.success || !res.data) {
        alert(res.message || 'Erro ao criar máquina');
        return null;
      }
      const nova = res.data as BudgetMaquina;
      atualizarObraLocal(obra.id, (o) => ({ ...o, maquinas: [...o.maquinas, { ...nova, gastos: nova.gastos ?? [] }] }));
      return nova.id;
    } catch (err) {
      console.error(err);
      alert('Erro ao criar máquina');
      return null;
    } finally {
      setSalvando(false);
    }
  };

  // Vai numa requisição só: o servidor grava UM ponto no histórico e apaga tudo
  // numa transação. Antes eram N requisições, e o histórico ficaria ilegível.
  const handleRemoverMaquinas = (ids: string[]) => {
    if (!obra) return;
    atualizarObraLocal(obra.id, (o) => ({ ...o, maquinas: o.maquinas.filter((m) => !ids.includes(m.id)) }));
    salvarSilencioso(() => apiService.removerMaquinasBudget(obra.id, ids));
  };

  // ===== Histórico de estado (desfazer que sobrevive a recarregar) =====

  const carregarHistorico = useCallback(async (obraId: string) => {
    setCarregandoHistorico(true);
    try {
      const res = await apiService.historicoBudget(obraId);
      if (res.success && res.data) {
        const dados = res.data as { pontos: PontoHistorico[]; atual: ResumoDoPonto | null };
        setHistorico(dados.pontos ?? []);
        setHistoricoAtual(dados.atual ?? null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCarregandoHistorico(false);
    }
  }, []);

  const abrirHistorico = () => {
    if (!obra) return;
    setMostrarHistorico(true);
    carregarHistorico(obra.id);
  };

  const restaurarPonto = async (ponto: PontoHistorico, agoraRecemLido?: ResumoDoPonto | null) => {
    if (!obra) return;
    // Quando vem do botão "Voltar atrás", o resumo acabou de ser lido e o state
    // ainda não atualizou — usar o do parâmetro evita mostrar número velho
    const agora = agoraRecemLido !== undefined ? agoraRecemLido : historicoAtual;
    const diferenca = agora
      ? `\nAgora: ${agora.linhas} linha(s), ${moeda(agora.budgetTotal)}\n`
        + `Voltando para: ${ponto.linhas} linha(s), ${moeda(ponto.budgetTotal)}\n`
      : '';
    if (!confirm(
      `Voltar a planilha "${obra.nome}" para ${tempoAtras(ponto.createdAt)}?\n\n`
      + `${ponto.descricao}\n${diferenca}\n`
      + 'O estado de agora vira um ponto novo, então dá para voltar atrás.'
    )) return;
    const ok = await executar(() => apiService.restaurarPontoBudget(ponto.id));
    if (ok) {
      await carregarHistorico(obra.id);
      setMostrarHistorico(false);
    }
  };

  /** Botão Desfazer: volta para o ponto mais recente */
  const desfazerUltimo = async () => {
    if (!obra) return;
    setCarregandoHistorico(true);
    try {
      const res = await apiService.historicoBudget(obra.id);
      const dados = res.success ? (res.data as { pontos: PontoHistorico[]; atual: ResumoDoPonto | null }) : null;
      const ponto = dados?.pontos?.[0];
      setHistorico(dados?.pontos ?? []);
      setHistoricoAtual(dados?.atual ?? null);
      if (!ponto) {
        alert('Ainda não há nada para desfazer nesta planilha.');
        return;
      }
      await restaurarPonto(ponto, dados?.atual ?? null);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  // ===== Operações estruturais (recarregam e reconstroem a planilha) =====

  const executar = async (acao: () => Promise<{ success: boolean; message?: string }>) => {
    setSalvando(true);
    try {
      const res = await acao();
      if (!res.success) {
        alert(res.message || 'Erro ao salvar');
        return false;
      }
      await carregar();
      setVersaoPlanilha((v) => v + 1);
      return true;
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar');
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const handleCriarObra = async () => {
    if (!novaObraNome.trim() || !projeto?.id) return;
    const ok = await executar(() => apiService.criarObraBudget(projeto.id, { nome: novaObraNome.trim() }));
    if (ok) {
      setNovaObraNome('');
      setMostrarNovaObra(false);
    }
  };

  const handleRemoverObra = async (o: BudgetObra) => {
    if (!confirm(`Remover a obra "${o.nome}" com ${o.maquinas.length} linha(s) de budget e todos os gastos?`)) return;
    await executar(() => apiService.removerObraBudget(o.id));
  };

  const handleCriarRubrica = async () => {
    if (!obra || !novaRubricaNome.trim()) return;
    setSalvando(true);
    try {
      const res = await apiService.criarRubricaBudget(obra.id, {
        nome: novaRubricaNome.trim(),
        vinculada: novaRubricaVinculada
      });
      if (!res.success) {
        alert(res.message || 'Erro ao salvar');
        return;
      }
      setNovaRubricaNome('');
      setNovaRubricaVinculada(true);
      setMostrarNovaRubrica(false);
      await carregar();
      setVersaoPlanilha((v) => v + 1);
      // A rubrica recém-criada entra ativa, senão não haveria onde digitar nela
      const nova = res.data as { id?: string } | undefined;
      if (nova?.id) setRubricasAtivas([nova.id]);
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Vincula/desvincula a rubrica. Nada é movido: o que muda é a planilha parar
   * de casar as linhas dela com as máquinas de mesmo nome e passar a mostrá-las
   * num bloco no final.
   */
  const handleAlternarVinculo = async (grupo: GrupoRubrica) => {
    if (!obra || grupo.id === SEM_RUBRICA) return;
    const ok = await executar(() => apiService.atualizarRubricaBudget(grupo.id, { vinculada: !grupo.vinculada }));
    if (ok) setRubricasAtivas((atual) => (atual.includes(grupo.id) ? atual : [...atual, grupo.id]));
  };

  const handleRemoverRubrica = async (grupo: GrupoRubrica) => {
    if (!obra || grupo.id === SEM_RUBRICA) return;
    if (!confirm(`Apagar a rubrica "${grupo.nome}" com o budget e os gastos lançados nela?`)) return;
    await executar(() => apiService.removerRubricaBudget(grupo.id));
  };

  const adicionarEtapa = () => {
    const nome = novaEtapaNome.trim();
    const rubricaId = novaEtapaRubrica || gruposCompletos[0]?.id;
    if (!nome || !rubricaId) return;
    setEtapasExtras((atual) => ({
      ...atual,
      [rubricaId]: [...(atual[rubricaId] ?? []), { key: nome, nome, tipo: novaEtapaTipo }]
    }));
    // A coluna nasce dentro de uma rubrica: se ela estiver fora do budget, entra
    setRubricasAtivas((atual) =>
      atual.includes(rubricaId) ? atual : gruposCompletos.filter((g) => atual.includes(g.id) || g.id === rubricaId).map((g) => g.id));
    setNovaEtapaNome('');
    setNovaEtapaTipo('realizado');
    setMostrarNovaEtapa(false);
  };

  const abrirEditarNomes = () => {
    if (!obra) return;
    setEditNomeObra(obra.nome);
    const rubricas: Record<string, string> = {};
    const etapas: Record<string, string> = {};
    gruposCompletos.forEach((g) => {
      rubricas[g.id] = g.nome;
      g.etapas.forEach((e) => { etapas[`${g.id}::${e.key}`] = e.nome ?? ''; });
    });
    setEditNomesRubricas(rubricas);
    setEditNomesEtapas(etapas);
    setMostrarEditarNomes(true);
  };

  const handleSalvarNomes = async () => {
    if (!obra) return;
    setSalvando(true);
    try {
      const nomeObra = editNomeObra.trim();
      if (nomeObra && nomeObra !== obra.nome) {
        const res = await apiService.atualizarObraBudget(obra.id, { nome: nomeObra });
        if (!res.success) {
          alert(res.message || 'Erro ao renomear a planilha');
          return;
        }
      }
      for (const grupo of gruposCompletos) {
        if (grupo.id === SEM_RUBRICA) continue;
        const novo = (editNomesRubricas[grupo.id] ?? '').trim();
        if (!novo || novo === grupo.nome) continue;
        const res = await apiService.atualizarRubricaBudget(grupo.id, { nome: novo });
        if (!res.success) {
          alert(res.message || `Erro ao renomear a rubrica "${grupo.nome}"`);
          return;
        }
      }
      // Etapas são globais na obra: renomear vale para toda a coluna
      const renomeadas = new Set<string>();
      for (const grupo of gruposCompletos) {
        for (const e of grupo.etapas) {
          const novo = (editNomesEtapas[`${grupo.id}::${e.key}`] ?? '').trim();
          if (!novo || novo === (e.nome ?? '') || renomeadas.has(e.key)) continue;
          const res = await apiService.renomearEtapaBudget(obra.id, { de: e.nome, para: novo });
          if (!res.success) {
            alert(res.message || `Erro ao renomear a coluna "${e.nome ?? 'Sem etapa'}"`);
            return;
          }
          renomeadas.add(e.key);
        }
      }
      setEtapasExtras({});
      setMostrarEditarNomes(false);
      await carregar();
      setVersaoPlanilha((v) => v + 1);
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar os nomes');
    } finally {
      setSalvando(false);
    }
  };

  const handleRemoverEtapa = async (grupo: GrupoRubrica, col: EtapaCol) => {
    if (!obra) return;
    if (!confirm(`Apagar a coluna "${col.nome ?? 'Sem etapa'}" e todos os valores lançados nela?`)) return;
    setSalvando(true);
    try {
      const res = await apiService.removerEtapaBudget(obra.id, col.nome);
      if (!res.success) {
        alert(res.message || 'Erro ao apagar a coluna');
        return;
      }
      setEtapasExtras((atual) => ({
        ...atual,
        [grupo.id]: (atual[grupo.id] ?? []).filter((x) => x.key !== col.key)
      }));
      setEditNomesEtapas((atual) =>
        Object.fromEntries(Object.entries(atual).filter(([k]) => k !== `${grupo.id}::${col.key}`)));
      await carregar();
      setVersaoPlanilha((v) => v + 1);
    } catch (err) {
      console.error(err);
      alert('Erro ao apagar a coluna');
    } finally {
      setSalvando(false);
    }
  };

  // ===== Juntar abas =====

  const abrirJuntar = () => {
    if (!obra || !resumo) return;
    const sugestoes: Record<string, string> = {};
    resumo.obras.filter((o) => o.id !== obra.id).forEach((o) => { sugestoes[o.id] = ''; });
    setJuntarOrigens(sugestoes);
    setJuntarRubricaDestino(gruposCompletos.some((g) => g.id === SEM_RUBRICA) ? obra.nome : '');
    setMostrarJuntar(true);
  };

  const juntarSelecionadas = Object.entries(juntarOrigens).filter(([, rubrica]) => rubrica.trim() !== '');

  const handleJuntar = async () => {
    if (!obra || juntarSelecionadas.length === 0) return;
    const origens = juntarSelecionadas.map(([obraId, rubrica]) => ({ obraId, rubrica: rubrica.trim() }));
    const nomes = origens.map((o) => o.rubrica);
    if (new Set(nomes).size !== nomes.length) {
      alert('Duas abas estão indo para a mesma rubrica. Use nomes diferentes.');
      return;
    }
    const resumoTexto = origens
      .map((o) => `• ${resumo?.obras.find((x) => x.id === o.obraId)?.nome} → rubrica "${o.rubrica}"`)
      .join('\n');
    if (!confirm(`Juntar em "${obra.nome}":\n\n${resumoTexto}\n\nAs linhas são movidas com budget e gastos intactos e as abas de origem, já vazias, são removidas.`)) return;
    const ok = await executar(() => apiService.juntarObrasBudget(obra.id, {
      origens,
      rubricaDestino: juntarRubricaDestino.trim() || null
    }));
    if (ok) setMostrarJuntar(false);
  };

  // ===== Lançamento em lote =====

  const grupoDoLote = gruposCompletos.find((g) => g.id === loteRubricaSel) ?? gruposCompletos[0];
  const etapasDoLote = grupoDoLote?.etapas ?? [];

  const abrirLote = () => {
    if (!obra) return;
    // Máquinas selecionadas na planilha (clique e arrasto) viram o alvo do lote
    const selecionadas = sheetRef.current?.getMaquinasSelecionadas() ?? [];
    setLoteSelecionadas(selecionadas);
    setLoteAlvo(selecionadas.length > 0 ? 'selecionadas' : 'todas');
    // Começa na rubrica que está em foco na planilha
    const primeiro = grupos[0] ?? gruposCompletos[0];
    setLoteRubricaSel(primeiro?.id ?? '');
    setLoteEtapaSel(primeiro?.etapas[0]?.key ?? '__nova__');
    setLoteNovaEtapaNome('');
    setLoteNovaEtapaTipo('realizado');
    setLoteValor('');
    setLoteModo('proporcional');
    setMostrarLote(true);
  };

  const iniciarSelecaoNaPlanilha = () => {
    setMostrarLote(false);
    setSelecionandoNaPlanilha(true);
  };

  const confirmarSelecaoNaPlanilha = () => {
    const selecionadas = sheetRef.current?.getMaquinasSelecionadas() ?? [];
    setLoteSelecionadas(selecionadas);
    if (selecionadas.length > 0) setLoteAlvo('selecionadas');
    setSelecionandoNaPlanilha(false);
    setMostrarLote(true);
  };

  /** Ids das linhas de budget da rubrica escolhida (o lote só mexe nela) */
  const idsDaRubricaDoLote = useMemo(() => {
    if (!obra || !grupoDoLote) return [];
    return obra.maquinas
      .filter((m) => (m.rubricaId ?? SEM_RUBRICA) === grupoDoLote.id)
      .map((m) => m.id);
  }, [obra, grupoDoLote]);

  const alvoDoLote = useMemo(() => {
    if (loteAlvo === 'todas') return idsDaRubricaDoLote;
    const permitidos = new Set(idsDaRubricaDoLote);
    return loteSelecionadas.filter((id) => permitidos.has(id));
  }, [loteAlvo, idsDaRubricaDoLote, loteSelecionadas]);

  const handleLancarLote = async () => {
    if (!obra) return;
    const valor = parseValor(loteValor);
    if (valor === null || valor <= 0) return;

    let etapaNome: string | undefined;
    let etapaTipo: 'realizado' | 'previsto' = 'realizado';
    if (loteEtapaSel === '__nova__') {
      etapaNome = loteNovaEtapaNome.trim() || undefined;
      etapaTipo = loteNovaEtapaTipo;
      if (!etapaNome) return;
    } else {
      const col = etapasDoLote.find((e) => e.key === loteEtapaSel);
      etapaNome = col?.nome ?? undefined;
      etapaTipo = col?.tipo === 'previsto' ? 'previsto' : 'realizado';
    }

    if (alvoDoLote.length === 0) {
      alert('Nenhuma linha desta rubrica no alvo escolhido.');
      return;
    }

    const ok = await executar(() => apiService.lancarGastosLoteBudget(obra.id, {
      valor,
      modo: loteModo,
      etapa: etapaNome,
      tipo: etapaTipo,
      // Sempre pula máquinas que já têm valor na etapa (nunca lança duas vezes)
      apenasSemValor: true,
      maquinaIds: alvoDoLote,
    }));
    if (ok) setMostrarLote(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
      </div>
    );
  }

  if (!resumo || !resumo.budgetHabilitado) {
    return (
      <div className="text-center py-12">
        <Wallet className="h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light mx-auto mb-4" />
        <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
          O controle de budget não está habilitado para este projeto.
        </p>
      </div>
    );
  }

  const cardCls = 'bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4';
  const inputCls = 'px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">Controle de Budget</h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            {resumo.projetoNome} — uma linha por máquina; cada rubrica (M.O, projeto mecânico, elétrico...) é um grupo de
            colunas. Rubrica desvinculada (despesas) tem linhas próprias, num bloco no final.
          </p>
        </div>
        {salvando && (
          <span className="inline-flex items-center gap-2 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            <Loader2 className="h-4 w-4 animate-spin" /> Salvando...
          </span>
        )}
      </div>

      {/* Abas de obras */}
      <div className="flex flex-wrap items-center gap-2">
        {resumo.obras.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setObraSelecionadaId(o.id)}
            className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors border-b-2 ${o.id === obraSelecionadaId
              ? 'bg-white dark:bg-schmersal-gray-darker text-schmersal-blue dark:text-schmersal-yellow border-schmersal-blue dark:border-schmersal-yellow shadow'
              : 'bg-schmersal-gray-light/50 dark:bg-schmersal-gray-dark/50 text-schmersal-gray-dark dark:text-white border-transparent hover:bg-white dark:hover:bg-schmersal-gray-darker'}`}
          >
            {o.nome}
          </button>
        ))}
        {mostrarNovaObra ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={novaObraNome}
              onChange={(e) => setNovaObraNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCriarObra(); }}
              placeholder="Nome da obra/cliente"
              autoFocus
              className={`${inputCls} text-sm py-1.5`}
            />
            <button type="button" onClick={handleCriarObra} disabled={salvando || !novaObraNome.trim()} className="p-2 rounded-lg bg-schmersal-blue text-white disabled:opacity-50" title="Criar obra">
              <Check className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setMostrarNovaObra(false)} className="p-2 rounded-lg text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:hover:text-white" title="Cancelar">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setMostrarNovaObra(true)}
            className="px-3 py-2 rounded-lg text-sm text-schmersal-blue dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> Nova obra
          </button>
        )}
      </div>

      {!obra ? (
        <div className="text-center py-12">
          <Wallet className="h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light mx-auto mb-4" />
          <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
            Nenhuma obra cadastrada. Crie a primeira obra/cliente para começar o controle.
          </p>
        </div>
      ) : (
        <>
          {/* Cards de resumo — somam apenas as rubricas ativas */}
          <div>
            <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mb-1.5">
              Somando {rubricasAtivas.length} de {gruposCompletos.length} rubrica(s):{' '}
              <strong className="text-schmersal-gray-dark dark:text-white">
                {grupos.map((g) => g.nome).join(' + ') || '—'}
              </strong>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className={cardCls}>
                <div className="flex items-center gap-2 text-schmersal-gray-medium dark:text-schmersal-gray-light text-xs font-medium">
                  <Wallet className="h-4 w-4" /> Budget total
                </div>
                <p className="mt-1.5 text-lg font-bold text-schmersal-gray-dark dark:text-white">{moeda(totais.budgetTotal)}</p>
              </div>
              <div className={cardCls}>
                <div className="flex items-center gap-2 text-schmersal-gray-medium dark:text-schmersal-gray-light text-xs font-medium">
                  <TrendingDown className="h-4 w-4" /> Gasto (realizado)
                </div>
                <p className="mt-1.5 text-lg font-bold text-schmersal-gray-dark dark:text-white">{moeda(totais.gastoRealizado)}</p>
              </div>
              <div className={cardCls}>
                <div className="flex items-center gap-2 text-schmersal-gray-medium dark:text-schmersal-gray-light text-xs font-medium">
                  <CalendarClock className="h-4 w-4" /> Previsto
                </div>
                <p className="mt-1.5 text-lg font-bold text-schmersal-gray-dark dark:text-white">{moeda(totais.gastoPrevisto)}</p>
              </div>
              <div className={cardCls}>
                <div className="flex items-center gap-2 text-schmersal-gray-medium dark:text-schmersal-gray-light text-xs font-medium">
                  <PiggyBank className="h-4 w-4" /> Saldo
                </div>
                <p className={`mt-1.5 text-lg font-bold ${totais.saldo < 0 ? 'text-red-500' : 'text-schmersal-gray-dark dark:text-white'}`}>
                  {moeda(totais.saldo)}
                </p>
              </div>
              <div className={cardCls}>
                <div className="flex items-center gap-2 text-schmersal-gray-medium dark:text-schmersal-gray-light text-xs font-medium">
                  <AlertTriangle className="h-4 w-4" /> Fora do budget
                </div>
                <p className={`mt-1.5 text-lg font-bold ${totais.foraDoBudget > 0 ? 'text-red-500' : 'text-schmersal-gray-dark dark:text-white'}`}>
                  {moeda(totais.foraDoBudget)}
                </p>
              </div>
            </div>
          </div>

          {/* Faixa de rubricas: clicar troca de rubrica; + soma ao budget, − tira */}
          {(obra.rubricas.length > 0 || gruposCompletos.length > 1) && (
            <div className={`${cardCls} !p-3`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide">
                  Rubricas do projeto
                </span>
                <span className="text-[11px] text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  clique no card para ver só ele · <strong>+</strong> soma ao budget · <strong>−</strong> tira ·
                  {' '}<strong>🔗</strong> desvincula (linhas próprias, no final da planilha)
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {gruposCompletos.map((g) => {
                  const dados = totaisDosGrupos[g.id];
                  const budget = dados?.budgetTotal ?? 0;
                  const gasto = dados?.gastoRealizado ?? 0;
                  const saldo = budget - gasto;
                  const ativa = rubricasAtivas.includes(g.id);
                  const unica = ativa && rubricasAtivas.length === 1;
                  const proporcao = budget > 0 ? Math.min(100, (gasto / budget) * 100) : 0;
                  return (
                    <div
                      key={g.id}
                      className={`relative rounded-lg border transition-colors min-w-[190px] ${ativa
                        ? 'border-schmersal-blue dark:border-schmersal-yellow bg-schmersal-blue/5 dark:bg-schmersal-yellow/5'
                        : 'border-dashed border-schmersal-gray-light dark:border-schmersal-gray-dark opacity-60 hover:opacity-100'}`}
                    >
                      <button
                        type="button"
                        onClick={() => focarRubrica(g.id)}
                        className="w-full text-left px-3 py-2 pr-9"
                        title={`Ver só a rubrica "${g.nome}"`}
                      >
                        <div className="text-xs font-semibold text-schmersal-gray-dark dark:text-white truncate">
                          {g.nome}
                        </div>
                        {!g.vinculada && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                            <Unlink className="h-3 w-3" /> desvinculada
                          </div>
                        )}
                        <div className="mt-1 text-[11px] text-schmersal-gray-medium dark:text-schmersal-gray-light">
                          {moeda(budget)} · gasto {moeda(gasto)}
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark overflow-hidden">
                          <div
                            className={`h-full rounded-full ${saldo < 0 ? 'bg-red-500' : 'bg-schmersal-blue dark:bg-schmersal-yellow'}`}
                            style={{ width: `${budget > 0 ? proporcao : 0}%` }}
                          />
                        </div>
                        <div className={`mt-1 text-[11px] font-semibold ${saldo < 0 ? 'text-red-500' : 'text-schmersal-gray-dark dark:text-white'}`}>
                          saldo {moeda(saldo)}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => alternarRubrica(g.id)}
                        disabled={unica}
                        className={`absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-md border text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${ativa
                          ? 'border-schmersal-blue text-schmersal-blue dark:border-schmersal-yellow dark:text-schmersal-yellow hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-yellow dark:hover:text-schmersal-gray-darker'
                          : 'border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-medium dark:text-schmersal-gray-light hover:border-schmersal-blue hover:text-schmersal-blue dark:hover:border-schmersal-yellow dark:hover:text-schmersal-yellow'}`}
                        title={ativa
                          ? (unica ? 'Deixe outra rubrica ativa para tirar esta' : `Tirar "${g.nome}" do budget`)
                          : `Somar "${g.nome}" ao budget`}
                      >
                        {ativa ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </button>
                      {g.id !== SEM_RUBRICA && (
                        <button
                          type="button"
                          onClick={() => handleAlternarVinculo(g)}
                          disabled={salvando}
                          className={`absolute bottom-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-md border transition-colors disabled:opacity-40 ${g.vinculada
                            ? 'border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-medium dark:text-schmersal-gray-light hover:border-amber-500 hover:text-amber-500'
                            : 'border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-500 hover:text-white'}`}
                          title={g.vinculada
                            ? `Desvincular "${g.nome}": as linhas dela saem do pivô das máquinas e viram um bloco no final`
                            : `Vincular "${g.nome}" de volta: as linhas voltam a casar com as máquinas de mesmo nome`}
                        >
                          {g.vinculada ? <Link2 className="h-3.5 w-3.5" /> : <Unlink className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Barra de ações */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={desfazerUltimo}
              disabled={salvando || carregandoHistorico}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-50"
              title="Volta a planilha inteira para o ponto anterior — exclusões, lotes, rubricas e gastos, mesmo depois de recarregar a página ou de outra pessoa ter mexido"
            >
              <Undo2 className="h-4 w-4 mr-1.5" /> Voltar atrás
            </button>
            <button
              type="button"
              onClick={abrirHistorico}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
              title="Histórico da planilha: todos os pontos para os quais dá para voltar"
            >
              <History className="h-4 w-4 mr-1.5" /> Histórico
            </button>
            <button
              type="button"
              onClick={abrirLote}
              className="inline-flex items-center px-3 py-1.5 bg-schmersal-blue text-white text-sm rounded-lg hover:bg-schmersal-blue-dark"
            >
              <Layers className="h-4 w-4 mr-1.5" /> Lançar em lote
            </button>
            {resumo.obras.length > 1 && (
              <button
                type="button"
                onClick={abrirJuntar}
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg border border-schmersal-blue text-schmersal-blue dark:border-schmersal-yellow dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
                title="Trazer outras abas para dentro desta como rubricas"
              >
                <Combine className="h-4 w-4 mr-1.5" /> Juntar abas
              </button>
            )}
            {mostrarNovaRubrica ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={novaRubricaNome}
                  onChange={(e) => setNovaRubricaNome(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCriarRubrica(); }}
                  placeholder="Nome da rubrica"
                  autoFocus
                  className={`${inputCls} text-sm py-1.5 w-44`}
                />
                <select
                  value={novaRubricaVinculada ? 'vinculada' : 'desvinculada'}
                  onChange={(e) => setNovaRubricaVinculada(e.target.value === 'vinculada')}
                  className={`${inputCls} text-sm py-1.5`}
                  title="Vinculada: a mesma lista de máquinas. Desvinculada: linhas próprias (despesas), num bloco no final."
                >
                  <option value="vinculada">Vinculada às máquinas</option>
                  <option value="desvinculada">Desvinculada (linhas próprias)</option>
                </select>
                <button type="button" onClick={handleCriarRubrica} disabled={salvando || !novaRubricaNome.trim()} className="p-2 rounded-lg bg-schmersal-blue text-white disabled:opacity-50" title="Criar rubrica">
                  <Check className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setMostrarNovaRubrica(false)} className="p-2 rounded-lg text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:hover:text-white" title="Cancelar">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMostrarNovaRubrica(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg text-schmersal-blue dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
              >
                <Plus className="h-4 w-4 mr-1" /> Rubrica
              </button>
            )}
            {mostrarNovaEtapa ? (
              <div className="flex items-center gap-2">
                <select value={novaEtapaRubrica} onChange={(e) => setNovaEtapaRubrica(e.target.value)} className={`${inputCls} text-sm py-1.5`}>
                  {gruposCompletos.map((g) => (
                    <option key={g.id} value={g.id}>{g.nome}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={novaEtapaNome}
                  onChange={(e) => setNovaEtapaNome(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') adicionarEtapa(); }}
                  placeholder="Nome da coluna/etapa"
                  autoFocus
                  className={`${inputCls} text-sm py-1.5 w-44`}
                />
                <select value={novaEtapaTipo} onChange={(e) => setNovaEtapaTipo(e.target.value as 'realizado' | 'previsto')} className={`${inputCls} text-sm py-1.5`}>
                  <option value="realizado">Realizado</option>
                  <option value="previsto">Previsto</option>
                </select>
                <button type="button" onClick={adicionarEtapa} disabled={!novaEtapaNome.trim()} className="p-2 rounded-lg bg-schmersal-blue text-white disabled:opacity-50" title="Adicionar coluna">
                  <Check className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setMostrarNovaEtapa(false)} className="p-2 rounded-lg text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:hover:text-white" title="Cancelar">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setNovaEtapaRubrica(gruposCompletos[0]?.id ?? ''); setMostrarNovaEtapa(true); }}
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg text-schmersal-blue dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
              >
                <Plus className="h-4 w-4 mr-1" /> Coluna
              </button>
            )}
            <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light ml-auto">
              {linhas.filter((l) => !l.grupoExclusivo).length} máquinas
              {linhas.some((l) => l.grupoExclusivo) &&
                ` · ${linhas.filter((l) => l.grupoExclusivo).length} item(ns) desvinculado(s) no final`}
              {' '}· {gruposCompletos.length} rubrica(s) · a linha em branco no fim entra no bloco logo acima dela
            </span>
            <button
              type="button"
              onClick={abrirEditarNomes}
              className="p-2 text-schmersal-blue dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg"
              title="Editar nomes da planilha, das rubricas e das colunas"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleRemoverObra(obra)}
              className="p-2 text-red-500 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg"
              title="Remover obra"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          {/* Planilha interativa */}
          <BudgetSheet
            ref={sheetRef}
            obraId={obra.id}
            obraNome={obra.nome}
            linhas={linhas}
            grupos={grupos}
            versao={versaoPlanilha}
            onRenomearMaquina={handleRenomearMaquina}
            onEditarBudget={handleEditarBudget}
            onEditarEtapa={handleEditarEtapa}
            onCriarLinha={handleCriarLinha}
            onRemoverMaquinas={handleRemoverMaquinas}
            onResync={() => { carregar().then(() => setVersaoPlanilha((v) => v + 1)); }}
            nomesDasRubricas={nomesDasRubricas}
          />
        </>
      )}

      {/* Barra flutuante do modo "selecionar na planilha" */}
      {selecionandoNaPlanilha && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl bg-schmersal-blue text-white">
          <span className="text-sm font-medium">
            Clique e arraste na planilha para selecionar as máquinas
          </span>
          <button
            type="button"
            onClick={confirmarSelecaoNaPlanilha}
            className="px-4 py-1.5 bg-white text-schmersal-blue text-sm font-semibold rounded-lg hover:bg-schmersal-gray-light"
          >
            Confirmar seleção
          </button>
          <button
            type="button"
            onClick={() => { setSelecionandoNaPlanilha(false); setMostrarLote(true); }}
            className="px-3 py-1.5 text-sm rounded-lg hover:bg-white/10"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Histórico de estado da planilha */}
      {mostrarHistorico && obra && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold text-schmersal-gray-dark dark:text-white">
                Histórico de "{obra.nome}"
              </h2>
              <button type="button" onClick={() => setMostrarHistorico(false)} className="text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
              Cada ponto é a planilha inteira como estava <strong>antes</strong> daquela operação — linhas, rubricas e
              gastos. Voltar para um ponto não apaga o resto: o estado de agora vira um ponto novo.
            </p>

            {historicoAtual && (
              <div className="mb-3 px-3 py-2 rounded-lg border border-schmersal-blue dark:border-schmersal-yellow bg-schmersal-blue/5 dark:bg-schmersal-yellow/5">
                <div className="text-xs font-semibold text-schmersal-blue dark:text-schmersal-yellow uppercase tracking-wide">Agora</div>
                <div className="text-sm text-schmersal-gray-dark dark:text-white">
                  {historicoAtual.linhas} linha(s) · {moeda(historicoAtual.budgetTotal)} de budget · {moeda(historicoAtual.gastoTotal)} em gastos
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto -mx-1 px-1">
              {carregandoHistorico ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-schmersal-blue" /></div>
              ) : historico.length === 0 ? (
                <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light py-8 text-center">
                  Nenhum ponto ainda. A partir da próxima alteração, cada operação deixa um ponto aqui.
                </p>
              ) : (
                <ul className="space-y-2">
                  {historico.map((ponto) => {
                    const perdeLinhas = historicoAtual ? ponto.linhas > historicoAtual.linhas : false;
                    return (
                      <li
                        key={ponto.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue dark:hover:border-schmersal-yellow"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-schmersal-gray-dark dark:text-white truncate" title={ponto.descricao}>
                            {ponto.descricao}
                          </div>
                          <div className="text-[11px] text-schmersal-gray-medium dark:text-schmersal-gray-light">
                            {tempoAtras(ponto.createdAt)}
                            {ponto.usuarioNome ? ` · ${ponto.usuarioNome}` : ''}
                            {' · '}{ponto.linhas} linha(s) · {moeda(ponto.budgetTotal)}
                            {perdeLinhas && (
                              <span className="ml-1 font-semibold text-amber-600 dark:text-amber-400">
                                (+{ponto.linhas - (historicoAtual?.linhas ?? 0)} linhas a recuperar)
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => restaurarPonto(ponto)}
                          disabled={salvando}
                          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-schmersal-blue text-schmersal-blue dark:border-schmersal-yellow dark:text-schmersal-yellow hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-yellow dark:hover:text-schmersal-gray-darker disabled:opacity-50"
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Voltar aqui
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal "Juntar abas" */}
      {mostrarJuntar && obra && resumo && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold text-schmersal-gray-dark dark:text-white">
                Juntar abas em "{obra.nome}"
              </h2>
              <button type="button" onClick={() => setMostrarJuntar(false)} className="text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
              Cada aba escolhida vira uma <strong>rubrica</strong> (grupo de colunas) aqui dentro. Máquinas com o mesmo
              nome passam a ocupar uma linha só. Budget e gastos são movidos como estão — nada é recalculado.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  Rubrica para o que já está nesta aba
                </label>
                <input
                  type="text"
                  value={juntarRubricaDestino}
                  onChange={(e) => setJuntarRubricaDestino(e.target.value)}
                  placeholder="Ex.: Escopo / Projeto base"
                  className={`${inputCls} w-full`}
                />
                <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
                  Em branco: as linhas atuais ficam sem rubrica, num grupo "Sem rubrica".
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-2">
                  Abas a trazer — preencha o nome da rubrica de cada uma
                </label>
                <div className="space-y-2">
                  {resumo.obras.filter((o) => o.id !== obra.id).map((o) => (
                    <div key={o.id} className="flex items-center gap-2">
                      <span className="text-sm flex-1 min-w-0 truncate text-schmersal-gray-dark dark:text-white" title={o.nome}>
                        {o.nome}
                        <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light ml-1.5">
                          ({o.maquinas.length} linha{o.maquinas.length === 1 ? '' : 's'} · {moeda(o.totais.budgetTotal)})
                        </span>
                      </span>
                      <span className="text-schmersal-gray-medium">→</span>
                      <input
                        type="text"
                        value={juntarOrigens[o.id] ?? ''}
                        onChange={(e) => setJuntarOrigens((atual) => ({ ...atual, [o.id]: e.target.value }))}
                        placeholder="deixe vazio para não juntar"
                        className={`${inputCls} text-sm py-1.5 w-56 shrink-0`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setMostrarJuntar(false)}
                disabled={salvando}
                className="px-4 py-2 text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleJuntar}
                disabled={salvando || juntarSelecionadas.length === 0}
                className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Juntar {juntarSelecionadas.length > 0 ? `${juntarSelecionadas.length} aba(s)` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edição de nomes (planilha, rubricas e colunas) */}
      {mostrarEditarNomes && obra && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-schmersal-gray-dark dark:text-white">Editar nomes</h2>
              <button type="button" onClick={() => setMostrarEditarNomes(false)} className="text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  Nome da planilha (obra)
                </label>
                <input
                  type="text"
                  value={editNomeObra}
                  onChange={(e) => setEditNomeObra(e.target.value)}
                  className={`${inputCls} w-full`}
                />
              </div>
              {gruposCompletos.map((grupo) => (
                <div key={grupo.id} className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg p-3">
                  <label className="block text-xs font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide mb-1">
                    Rubrica
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editNomesRubricas[grupo.id] ?? ''}
                      onChange={(e) => setEditNomesRubricas((atual) => ({ ...atual, [grupo.id]: e.target.value }))}
                      disabled={grupo.id === SEM_RUBRICA}
                      placeholder={grupo.nome}
                      className={`${inputCls} flex-1 min-w-0 text-sm py-1.5 disabled:opacity-60`}
                    />
                    {grupo.id !== SEM_RUBRICA && (
                      <button
                        type="button"
                        onClick={() => handleRemoverRubrica(grupo)}
                        disabled={salvando}
                        className="p-2 shrink-0 text-red-500 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg disabled:opacity-50"
                        title="Apagar a rubrica com o budget e os gastos lançados nela"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <label className="block text-xs font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide mt-3 mb-1">
                    Colunas
                  </label>
                  <div className="space-y-2">
                    {grupo.etapas.map((e) => (
                      <div key={e.key} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editNomesEtapas[`${grupo.id}::${e.key}`] ?? ''}
                          onChange={(ev) => setEditNomesEtapas((atual) => ({ ...atual, [`${grupo.id}::${e.key}`]: ev.target.value }))}
                          placeholder={e.nome ?? 'Sem etapa'}
                          className={`${inputCls} flex-1 min-w-0 text-sm py-1.5`}
                        />
                        <span className="text-xs w-16 shrink-0 text-schmersal-gray-medium dark:text-schmersal-gray-light">
                          {e.tipo === 'previsto' ? 'previsto' : 'realizado'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoverEtapa(grupo, e)}
                          disabled={salvando}
                          className="p-2 shrink-0 text-red-500 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg disabled:opacity-50"
                          title="Apagar coluna e todos os valores lançados nela"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                Renomear a coluna atualiza os lançamentos daquela etapa em toda a obra. Apagar remove a coluna e os valores lançados nela.
              </p>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setMostrarEditarNomes(false)}
                disabled={salvando}
                className="px-4 py-2 text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSalvarNomes}
                disabled={salvando || !editNomeObra.trim()}
                className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de lançamento em lote */}
      {mostrarLote && obra && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-schmersal-gray-dark dark:text-white">
                Lançar em lote — {obra.nome}
              </h2>
              <button type="button" onClick={() => setMostrarLote(false)} className="text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                    Valor (R$) *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={loteValor}
                    onChange={(e) => setLoteValor(e.target.value)}
                    placeholder="0,00"
                    autoFocus
                    className={`${inputCls} w-full`}
                  />
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                    Rubrica
                  </label>
                  <select
                    value={loteRubricaSel}
                    onChange={(e) => {
                      setLoteRubricaSel(e.target.value);
                      const g = gruposCompletos.find((x) => x.id === e.target.value);
                      setLoteEtapaSel(g?.etapas[0]?.key ?? '__nova__');
                    }}
                    className={`${inputCls} w-full max-w-full`}
                  >
                    {gruposCompletos.map((g) => (
                      <option key={g.id} value={g.id}>{g.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                    Etapa
                  </label>
                  <select value={loteEtapaSel} onChange={(e) => setLoteEtapaSel(e.target.value)} className={`${inputCls} w-full max-w-full`}>
                    {etapasDoLote.map((e) => (
                      <option key={e.key} value={e.key}>{e.nome ?? 'Sem etapa'}</option>
                    ))}
                    <option value="__nova__">➕ Criar outra etapa...</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                    Como aplicar
                  </label>
                  <select value={loteModo} onChange={(e) => setLoteModo(e.target.value as 'por_maquina' | 'rateio' | 'proporcional')} className={`${inputCls} w-full max-w-full`}>
                    <option value="proporcional">Total distribuído PROPORCIONAL ao budget</option>
                    <option value="rateio">Total dividido IGUALMENTE entre as máquinas</option>
                    <option value="por_maquina">Este valor para CADA máquina</option>
                  </select>
                </div>
                {loteEtapaSel === '__nova__' && (
                  <div className="flex gap-2 min-w-0 md:col-span-2">
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                        Nome da nova etapa
                      </label>
                      <input
                        type="text"
                        value={loteNovaEtapaNome}
                        onChange={(e) => setLoteNovaEtapaNome(e.target.value)}
                        placeholder="Ex.: 3ª etapa, Julho..."
                        className={`${inputCls} w-full`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                        Tipo
                      </label>
                      <select value={loteNovaEtapaTipo} onChange={(e) => setLoteNovaEtapaTipo(e.target.value as 'realizado' | 'previsto')} className={`${inputCls}`}>
                        <option value="realizado">Realizado</option>
                        <option value="previsto">Previsto</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white">
                    Aplicar em
                  </label>
                  <button
                    type="button"
                    onClick={iniciarSelecaoNaPlanilha}
                    className="text-xs px-2.5 py-1 rounded-lg bg-schmersal-blue/10 text-schmersal-blue dark:bg-schmersal-yellow/10 dark:text-schmersal-yellow hover:bg-schmersal-blue/20 font-medium"
                  >
                    🖱 Selecionar na planilha
                  </button>
                </div>
                <div className="space-y-1.5">
                  <label className={`flex items-center gap-2 text-sm rounded px-1.5 py-0.5 ${loteSelecionadas.length === 0 ? 'opacity-50' : 'cursor-pointer'} text-schmersal-gray-dark dark:text-white`}>
                    <input
                      type="radio"
                      name="lote-alvo"
                      checked={loteAlvo === 'selecionadas'}
                      disabled={loteSelecionadas.length === 0}
                      onChange={() => setLoteAlvo('selecionadas')}
                    />
                    Máquinas selecionadas na planilha
                    {loteSelecionadas.length === 0 && (
                      <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">— use o botão acima</span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer rounded px-1.5 py-0.5 text-schmersal-gray-dark dark:text-white">
                    <input
                      type="radio"
                      name="lote-alvo"
                      checked={loteAlvo === 'todas'}
                      onChange={() => setLoteAlvo('todas')}
                    />
                    Todas as máquinas da rubrica
                  </label>
                </div>
                <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1.5">
                  O lançamento entra só na rubrica <strong>{grupoDoLote?.nome ?? '—'}</strong> — {alvoDoLote.length} linha(s) alvo.
                </p>
              </div>
              {(() => {
                const valor = parseValor(loteValor);
                const alvoQtd = alvoDoLote.length;
                if (valor === null || valor <= 0 || alvoQtd === 0) return null;
                if (loteModo === 'proporcional') {
                  return (
                    <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                      O total de {moeda(valor)} será distribuído entre as {alvoQtd} máquina(s) na proporção do budget de cada uma.
                    </p>
                  );
                }
                const porMaquina = loteModo === 'rateio' ? valor / alvoQtd : valor;
                return (
                  <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    Cada máquina receberá {moeda(porMaquina)}.
                  </p>
                );
              })()}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setMostrarLote(false)}
                disabled={salvando}
                className="px-4 py-2 text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleLancarLote}
                disabled={salvando || parseValor(loteValor) === null || (parseValor(loteValor) ?? 0) <= 0 || alvoDoLote.length === 0 || (loteEtapaSel === '__nova__' && !loteNovaEtapaNome.trim())}
                className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Lançar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
